import { StatusBar } from "expo-status-bar";
import {
  Alert,
  Image,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  useColorScheme,
} from "react-native";
import AsyncStorage from "@react-native-async-storage/async-storage";
import { useEffect, useMemo, useState } from "react";
import { View } from "../components/Themed";
import { gql, useMutation, useQuery } from "@apollo/client";
import { Picker } from "@react-native-picker/picker";
import { MaterialIcons } from "@expo/vector-icons";
import { v4 as uuidv4 } from "uuid";
import Config from "../constants/Config";
import * as ImagePicker from "expo-image-picker";
import { getProductImageUrl } from "../utils/urls";

const KIOSK_DEVICES_QUERY = gql`
  query kioskDevices($token: String!) {
    kioskDevices(token: $token) {
      id
      code
      kioskName
      operatorId
      logoUrl
      defaultPosId
      adUrl
      enabled
      createdAt
      updatedAt
    }
  }
`;

const OPERATORS_QUERY = gql`
  query getOperators($token: String!) {
    operators(token: $token) {
      id
      name
      role
    }
  }
`;

const CREATE_KIOSK_DEVICE = gql`
  mutation createKioskDevice(
    $token: String!
    $operator: ID!
    $id: ID!
    $code: String!
    $kioskName: String!
    $operatorId: ID!
    $logoUrl: String
    $defaultPosId: String
    $adUrl: String
    $enabled: Boolean
  ) {
    createKioskDevice(
      token: $token
      operator: $operator
      id: $id
      code: $code
      kioskName: $kioskName
      operatorId: $operatorId
      logoUrl: $logoUrl
      defaultPosId: $defaultPosId
      adUrl: $adUrl
      enabled: $enabled
    ) {
      id
    }
  }
`;

const UPDATE_KIOSK_DEVICE = gql`
  mutation updateKioskDevice(
    $token: String!
    $operator: ID!
    $id: ID!
    $code: String
    $kioskName: String
    $operatorId: ID
    $logoUrl: String
    $defaultPosId: String
    $adUrl: String
    $enabled: Boolean
  ) {
    updateKioskDevice(
      token: $token
      operator: $operator
      id: $id
      code: $code
      kioskName: $kioskName
      operatorId: $operatorId
      logoUrl: $logoUrl
      defaultPosId: $defaultPosId
      adUrl: $adUrl
      enabled: $enabled
    ) {
      id
      enabled
    }
  }
`;

const DELETE_KIOSK_DEVICE = gql`
  mutation deleteKioskDevice($token: String!, $operator: ID!, $id: ID!) {
    deleteKioskDevice(token: $token, operator: $operator, id: $id)
  }
`;

const UPLOAD_KIOSK_DEVICE_LOGO = gql`
  mutation uploadKioskDeviceLogo($token: String!, $operator: ID!, $file: Upload!) {
    uploadKioskDeviceLogo(token: $token, operator: $operator, file: $file)
  }
`;

function generateDeviceCode() {
  return `KIOSK-${Math.random().toString(36).slice(2, 8).toUpperCase()}`;
}

export default function OperatorSettingsKioskDevicesScreen() {
  const [operatorId, setOperatorId] = useState("");
  const [token, setToken] = useState("");
  const [adminToken, setAdminToken] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [editingDevice, setEditingDevice] = useState(null);
  const [code, setCode] = useState("");
  const [kioskName, setKioskName] = useState("");
  const [assignedOperatorId, setAssignedOperatorId] = useState("");
  const [logoUrl, setLogoUrl] = useState("");
  const [defaultPosId, setDefaultPosId] = useState("");
  const [adUrl, setAdUrl] = useState("");
  const [enabled, setEnabled] = useState(true);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const colorScheme = useColorScheme();

  useEffect(() => {
    async function fetchData() {
      try {
        const storedId = await AsyncStorage.getItem("operatorId");
        setOperatorId(storedId || "");
      } catch (_ex) {}

      try {
        const storedToken = await AsyncStorage.getItem("token");
        setToken(storedToken || "");
      } catch (_ex) {}

      try {
        const storedAdminToken = await AsyncStorage.getItem("adminToken");
        setAdminToken(storedAdminToken || "");
      } catch (_ex) {}
    }
    fetchData();
  }, []);

  const {
    loading: loadingDevices,
    error: devicesError,
    data: devicesData,
    refetch: refetchDevices,
  } = useQuery(KIOSK_DEVICES_QUERY, {
    variables: { token: adminToken },
    skip: !adminToken,
  });

  const { data: operatorsData } = useQuery(OPERATORS_QUERY, {
    variables: { token },
    skip: !token,
  });

  const [createKioskDevice] = useMutation(CREATE_KIOSK_DEVICE);
  const [updateKioskDevice] = useMutation(UPDATE_KIOSK_DEVICE);
  const [deleteKioskDevice] = useMutation(DELETE_KIOSK_DEVICE);
  const [uploadKioskDeviceLogo] = useMutation(UPLOAD_KIOSK_DEVICE_LOGO);

  const operators = operatorsData?.operators || [];
  const operatorNameById = useMemo(() => {
    return operators.reduce((acc, operator) => {
      acc[operator.id] = operator.name || operator.id;
      return acc;
    }, {});
  }, [operators]);

  const resetForm = () => {
    setEditingDevice(null);
    setCode("");
    setKioskName("");
    setAssignedOperatorId("");
    setLogoUrl("");
    setDefaultPosId("");
    setAdUrl("");
    setEnabled(true);
    setShowForm(false);
  };

  const startCreate = () => {
    setEditingDevice(null);
    setCode(generateDeviceCode());
    setKioskName("");
    setAssignedOperatorId(operators[0]?.id || "");
    setLogoUrl("");
    setDefaultPosId("");
    setAdUrl("");
    setEnabled(true);
    setShowForm(true);
  };

  const startEdit = (device) => {
    setEditingDevice(device);
    setCode(device.code || "");
    setKioskName(device.kioskName || "");
    setAssignedOperatorId(device.operatorId || "");
    setLogoUrl(device.logoUrl || "");
    setDefaultPosId(device.defaultPosId || "");
    setAdUrl(device.adUrl || "");
    setEnabled(device.enabled !== false);
    setShowForm(true);
  };

  const handleUploadLogo = async () => {
    if (!adminToken || !operatorId) {
      Alert.alert("Unauthorized", "Admin login is required to upload a logo.");
      return;
    }

    try {
      const { status } = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (status !== "granted") {
        Alert.alert("Permission Required", "Permission to access media library is required.");
        return;
      }

      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsEditing: true,
        quality: 0.82,
        base64: true,
      });

      if (result.canceled || !result.assets?.[0]) {
        return;
      }

      const asset = result.assets[0];
      const mime = asset.mimeType || "image/jpeg";
      if (!asset.base64) {
        Alert.alert("Upload Failed", "Selected image did not include base64 data.");
        return;
      }

      const dataUri = `data:${mime};base64,${asset.base64}`;
      setUploadingLogo(true);
      const response = await uploadKioskDeviceLogo({
        variables: {
          token: adminToken,
          operator: operatorId,
          file: { uri: dataUri },
        },
      });
      const uploadedUrl = response?.data?.uploadKioskDeviceLogo;
      if (!uploadedUrl) {
        throw new Error("Server did not return logo URL.");
      }
      setLogoUrl(uploadedUrl);
    } catch (err) {
      Alert.alert("Upload Failed", err.message || "Could not upload kiosk logo.");
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleSave = async () => {
    const normalizedCode = String(code || "").trim().toUpperCase();
    const normalizedName = String(kioskName || "").trim();
    const normalizedOperatorId = String(assignedOperatorId || "").trim();

    if (!normalizedCode || !normalizedName || !normalizedOperatorId) {
      Alert.alert("Missing fields", "Code, kiosk name and assigned operator are required.");
      return;
    }

    try {
      if (editingDevice) {
        await updateKioskDevice({
          variables: {
            token: adminToken,
            operator: operatorId,
            id: editingDevice.id,
            code: normalizedCode,
            kioskName: normalizedName,
            operatorId: normalizedOperatorId,
            logoUrl: logoUrl.trim() || null,
            defaultPosId: defaultPosId.trim() || null,
            adUrl: adUrl.trim() || null,
            enabled,
          },
        });
      } else {
        await createKioskDevice({
          variables: {
            token: adminToken,
            operator: operatorId,
            id: uuidv4(),
            code: normalizedCode,
            kioskName: normalizedName,
            operatorId: normalizedOperatorId,
            logoUrl: logoUrl.trim() || null,
            defaultPosId: defaultPosId.trim() || null,
            adUrl: adUrl.trim() || null,
            enabled,
          },
        });
      }

      resetForm();
      refetchDevices();
    } catch (err) {
      Alert.alert("Error", err.message || "Failed to save kiosk device.");
    }
  };

  const handleDelete = (device) => {
    Alert.alert(
      "Delete kiosk device",
      `Delete "${device.kioskName}" (${device.code})?`,
      [
        { text: "Cancel", style: "cancel" },
        {
          text: "Delete",
          style: "destructive",
          onPress: async () => {
            try {
              await deleteKioskDevice({
                variables: {
                  token: adminToken,
                  operator: operatorId,
                  id: device.id,
                },
              });
              refetchDevices();
            } catch (err) {
              Alert.alert("Error", err.message || "Failed to delete kiosk device.");
            }
          },
        },
      ]
    );
  };

  const handleToggleEnabled = async (device) => {
    try {
      await updateKioskDevice({
        variables: {
          token: adminToken,
          operator: operatorId,
          id: device.id,
          enabled: device.enabled === false,
        },
      });
      refetchDevices();
    } catch (err) {
      Alert.alert("Error", err.message || "Failed to update device.");
    }
  };

  const textColor = colorScheme === "dark" ? "white" : "black";
  const cardBg = colorScheme === "dark" ? "#1f1f1f" : "white";
  const mutedColor = colorScheme === "dark" ? "#bbb" : "#666";
  const devices = devicesData?.kioskDevices || [];

  return (
    <ScrollView style={styles.container}>
      <View style={{ backgroundColor: "transparent" }}>
        <View style={styles.header}>
          <Text style={[styles.title, { color: textColor }]}>Kiosk Devices</Text>
          {!showForm && adminToken ? (
            <Pressable
              style={[styles.addButton, { backgroundColor: Config.companyColor || "#007AFF" }]}
              onPress={startCreate}
            >
              <MaterialIcons name="add" size={20} color="white" />
              <Text style={styles.addButtonText}>Add Device</Text>
            </Pressable>
          ) : null}
        </View>

        {!adminToken ? (
          <Text style={[styles.infoText, { color: mutedColor }]}>
            Admin login is required to manage kiosk devices.
          </Text>
        ) : null}

        {showForm ? (
          <View style={[styles.form, { backgroundColor: cardBg }]}>
            <Text style={[styles.formTitle, { color: textColor }]}>
              {editingDevice ? "Edit Device" : "New Device"}
            </Text>

            <TextInput
              value={code}
              onChangeText={(value) => setCode(String(value || "").toUpperCase())}
              placeholder="Device code"
              placeholderTextColor={mutedColor}
              style={[styles.input, { color: textColor, backgroundColor: colorScheme === "dark" ? "#2a2a2a" : "#f3f4f6" }]}
              autoCapitalize="characters"
            />
            <Pressable style={styles.ghostButton} onPress={() => setCode(generateDeviceCode())}>
              <Text style={styles.ghostButtonText}>Generate Code</Text>
            </Pressable>

            <TextInput
              value={kioskName}
              onChangeText={setKioskName}
              placeholder="Kiosk name"
              placeholderTextColor={mutedColor}
              style={[styles.input, { color: textColor, backgroundColor: colorScheme === "dark" ? "#2a2a2a" : "#f3f4f6" }]}
            />

            <View style={[styles.pickerWrap, { backgroundColor: colorScheme === "dark" ? "#2a2a2a" : "#f3f4f6" }]}>
              <Picker selectedValue={assignedOperatorId} onValueChange={setAssignedOperatorId} style={{ color: textColor }}>
                <Picker.Item label="Select operator" value="" />
                {operators.map((operator) => (
                  <Picker.Item
                    key={operator.id}
                    label={`${operator.name || operator.id} (${operator.role || "operator"})`}
                    value={operator.id}
                  />
                ))}
              </Picker>
            </View>

            <View style={styles.logoUploadSection}>
              <Text style={[styles.logoUploadLabel, { color: textColor }]}>Logo (optional)</Text>
              {logoUrl ? (
                <Image source={{ uri: getProductImageUrl(logoUrl) }} style={styles.logoPreview} resizeMode="contain" />
              ) : (
                <View style={[styles.logoPlaceholder, { borderColor: colorScheme === "dark" ? "#525252" : "#d1d5db" }]}>
                  <MaterialIcons name="image" size={24} color={mutedColor} />
                  <Text style={[styles.logoPlaceholderText, { color: mutedColor }]}>No logo selected</Text>
                </View>
              )}
              <View style={styles.row}>
                <Pressable
                  style={[styles.button, styles.neutralButton]}
                  onPress={handleUploadLogo}
                  disabled={uploadingLogo}
                >
                  <Text style={styles.neutralButtonText}>
                    {uploadingLogo ? "Uploading..." : (logoUrl ? "Replace Logo" : "Upload Logo")}
                  </Text>
                </Pressable>
                {logoUrl ? (
                  <Pressable
                    style={[styles.button, styles.cancelButton]}
                    onPress={() => setLogoUrl("")}
                    disabled={uploadingLogo}
                  >
                    <Text style={styles.cancelButtonText}>Remove</Text>
                  </Pressable>
                ) : null}
              </View>
            </View>

            <TextInput
              value={defaultPosId}
              onChangeText={setDefaultPosId}
              placeholder="Default POS ID (optional)"
              placeholderTextColor={mutedColor}
              style={[styles.input, { color: textColor, backgroundColor: colorScheme === "dark" ? "#2a2a2a" : "#f3f4f6" }]}
            />

            <TextInput
              value={adUrl}
              onChangeText={setAdUrl}
              placeholder="Ad URL (optional)"
              placeholderTextColor={mutedColor}
              autoCapitalize="none"
              autoCorrect={false}
              style={[styles.input, { color: textColor, backgroundColor: colorScheme === "dark" ? "#2a2a2a" : "#f3f4f6" }]}
            />

            <Pressable style={styles.toggleRow} onPress={() => setEnabled((previous) => !previous)}>
              <MaterialIcons
                name={enabled ? "check-box" : "check-box-outline-blank"}
                size={22}
                color={enabled ? "#16a34a" : mutedColor}
              />
              <Text style={[styles.toggleText, { color: textColor }]}>Enabled for login</Text>
            </Pressable>

            <View style={styles.row}>
              <Pressable style={[styles.button, styles.cancelButton]} onPress={resetForm}>
                <Text style={styles.cancelButtonText}>Cancel</Text>
              </Pressable>
              <Pressable
                style={[styles.button, styles.saveButton, { backgroundColor: Config.companyColor || "#007AFF" }]}
                onPress={handleSave}
              >
                <Text style={styles.saveButtonText}>{editingDevice ? "Update" : "Create"}</Text>
              </Pressable>
            </View>
          </View>
        ) : null}

        {loadingDevices ? <Text style={[styles.infoText, { color: mutedColor }]}>Loading kiosk devices...</Text> : null}
        {devicesError ? <Text style={styles.errorText}>{devicesError.message}</Text> : null}

        {devices.map((device) => (
          <View key={device.id} style={[styles.card, { backgroundColor: cardBg }]}>
            <View style={styles.cardHeader}>
              <Text style={[styles.cardTitle, { color: textColor }]}>{device.kioskName}</Text>
              <Text style={[styles.status, { color: device.enabled ? "#16a34a" : "#b91c1c" }]}>
                {device.enabled ? "Enabled" : "Disabled"}
              </Text>
            </View>
            <Text style={[styles.meta, { color: mutedColor }]}>Code: {device.code}</Text>
            <Text style={[styles.meta, { color: mutedColor }]}>
              Operator: {operatorNameById[device.operatorId] || device.operatorId}
            </Text>
            {device.defaultPosId ? (
              <Text style={[styles.meta, { color: mutedColor }]}>Default POS: {device.defaultPosId}</Text>
            ) : null}
            {device.adUrl ? (
              <Text style={[styles.meta, { color: mutedColor }]}>Ad URL: {device.adUrl}</Text>
            ) : null}
            {device.logoUrl ? (
              <Image source={{ uri: getProductImageUrl(device.logoUrl) }} style={styles.logoCardPreview} resizeMode="contain" />
            ) : null}

            <View style={styles.row}>
              <Pressable style={[styles.button, styles.neutralButton]} onPress={() => startEdit(device)}>
                <Text style={styles.neutralButtonText}>Edit</Text>
              </Pressable>
              <Pressable
                style={[styles.button, styles.toggleButton]}
                onPress={() => handleToggleEnabled(device)}
              >
                <Text style={styles.toggleButtonText}>{device.enabled ? "Disable" : "Enable"}</Text>
              </Pressable>
              <Pressable style={[styles.button, styles.deleteButton]} onPress={() => handleDelete(device)}>
                <Text style={styles.deleteButtonText}>Delete</Text>
              </Pressable>
            </View>
          </View>
        ))}

        {!loadingDevices && devices.length === 0 ? (
          <Text style={[styles.infoText, { color: mutedColor }]}>
            No kiosk devices yet. Create one to generate a login code.
          </Text>
        ) : null}
      </View>
      <StatusBar style={Platform.OS === "ios" ? "light" : "auto"} />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    padding: 16,
  },
  header: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 14,
    backgroundColor: "transparent",
  },
  title: {
    fontSize: 22,
    fontWeight: "700",
  },
  addButton: {
    flexDirection: "row",
    alignItems: "center",
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 6,
  },
  addButtonText: {
    color: "white",
    fontWeight: "700",
  },
  form: {
    borderRadius: 12,
    padding: 12,
    marginBottom: 16,
  },
  formTitle: {
    fontSize: 18,
    fontWeight: "700",
    marginBottom: 10,
  },
  input: {
    borderRadius: 10,
    paddingHorizontal: 12,
    paddingVertical: 10,
    marginBottom: 10,
    fontSize: 15,
  },
  pickerWrap: {
    borderRadius: 10,
    marginBottom: 10,
  },
  ghostButton: {
    alignSelf: "flex-start",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#93c5fd",
    backgroundColor: "#eff6ff",
    paddingHorizontal: 10,
    paddingVertical: 6,
    marginBottom: 10,
  },
  ghostButtonText: {
    color: "#1d4ed8",
    fontWeight: "600",
  },
  logoUploadSection: {
    marginBottom: 10,
    backgroundColor: "transparent",
  },
  logoUploadLabel: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 8,
  },
  logoPreview: {
    width: "100%",
    height: 112,
    borderRadius: 10,
    backgroundColor: "#f8fafc",
    marginBottom: 8,
  },
  logoPlaceholder: {
    height: 112,
    borderRadius: 10,
    borderWidth: 1,
    borderStyle: "dashed",
    alignItems: "center",
    justifyContent: "center",
    marginBottom: 8,
    backgroundColor: "transparent",
  },
  logoPlaceholderText: {
    fontSize: 12,
    marginTop: 6,
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
    marginBottom: 12,
    backgroundColor: "transparent",
  },
  toggleText: {
    fontSize: 14,
    fontWeight: "600",
  },
  row: {
    flexDirection: "row",
    gap: 8,
    backgroundColor: "transparent",
  },
  button: {
    flex: 1,
    borderRadius: 10,
    paddingVertical: 10,
    alignItems: "center",
  },
  saveButton: {},
  saveButtonText: {
    color: "white",
    fontWeight: "700",
  },
  cancelButton: {
    backgroundColor: "#e5e7eb",
  },
  cancelButtonText: {
    color: "#111827",
    fontWeight: "700",
  },
  neutralButton: {
    backgroundColor: "#e0f2fe",
  },
  neutralButtonText: {
    color: "#0c4a6e",
    fontWeight: "700",
  },
  toggleButton: {
    backgroundColor: "#fef3c7",
  },
  toggleButtonText: {
    color: "#92400e",
    fontWeight: "700",
  },
  deleteButton: {
    backgroundColor: "#fee2e2",
  },
  deleteButtonText: {
    color: "#b91c1c",
    fontWeight: "700",
  },
  card: {
    borderRadius: 12,
    padding: 12,
    marginBottom: 10,
  },
  cardHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    marginBottom: 6,
    backgroundColor: "transparent",
  },
  cardTitle: {
    fontSize: 16,
    fontWeight: "700",
    flex: 1,
    marginRight: 8,
  },
  status: {
    fontSize: 12,
    fontWeight: "700",
    textTransform: "uppercase",
  },
  meta: {
    fontSize: 13,
    marginBottom: 3,
  },
  logoCardPreview: {
    width: "100%",
    height: 72,
    borderRadius: 8,
    backgroundColor: "#f8fafc",
    marginTop: 6,
    marginBottom: 8,
  },
  infoText: {
    paddingVertical: 12,
    textAlign: "center",
    fontSize: 14,
  },
  errorText: {
    color: "#dc2626",
    paddingVertical: 10,
    textAlign: "center",
  },
});
